import { lazy, ReactNode, Suspense, SyntheticEvent, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ScopedCssBaseline, Skeleton, ThemeProvider } from '@mui/material';

import { RoleEnum, Roles } from '@youscience/commonapp-service-common';
import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';
import { brightpathTheme } from '@youscience/theme';

import useCommonAppAdminStore from '@store/CommonAppAdminStore';
import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';

import useAppInitStore from '@store/AppInitStore';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { Chip } from '@components/Chip';

import { ECP_HASH } from '@constants';
import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { STATUS_CHIP_COLORS } from '@constants/recommenderWorkspace';
import { ROUTES } from '@constants/ROUTES';

import { useUserProfile } from '@hooks/useUserProfile';

import { formatDateWithFallback } from '@utils/formatter';

import CommonAppLogo from '@images/common-app-logo-button.svg?react';
import InfoIcon from '@images/info.svg?react';
import FlagIcon from '@images/sports-score.svg?react';

const CommonAppApplicationForms = lazy(() => import('./components/CommonAppApplicationForms'));
const CommonAppColleges = lazy(() => import('./components/CommonAppColleges'));
const CommonAppTeacherEvaluation = lazy(() => import('./components/CommonAppTeacherEvaluation'));

import {
  StyledCommonAppId,
  StyledHeadInfo,
  StyledHeadSectionInner,
  StyledInfoItem,
  StyledTab,
  StyledTabLabel,
  StyledTabs,
  sxStyles,
} from './StudentDetailsPage.styles';
import { useApplicantFormsMetadata } from './hooks/useApplicantFormsMetadata';
import { getGeneralStudentStatus } from './components/common/formUtils';

const renderInfoItem = (title: string, value: string, icon?: ReactNode) => {
  return (
    <StyledInfoItem>
      {icon ? <CoreBox>{icon}</CoreBox> : null}

      <CoreTypography sx={sxStyles.infoItemTitle} variant="subtitle2" component="h4">
        {title}
      </CoreTypography>

      <CoreTypography sx={sxStyles.infoItemValue} variant="body2">
        {value}
      </CoreTypography>
    </StyledInfoItem>
  );
};

const renderTabItem = (label: ReactNode) => (
  <StyledTabLabel>
    <CommonAppLogo />

    {label}
  </StyledTabLabel>
);

export const StudentDetailsPage = () => {
  const params = useParams();
  const { hash } = useLocation();

  useAppInitStore((store) => store);
  const { userRoles, getRoles } = useCommonAppAdminStore((store) => store);
  const { formsMetadata, isLoading: formsMetadataLoading } = useApplicantFormsMetadata();

  const { organizationName = '' } = useUserProfile();
  const { getRecommenderProfile, getUserProfile, userProfile } = useRecommenderStore();
  const {
    activeTabs,
    setActiveTabs,
    applicantFormsAvailable,
    getApplicantFormsAvailable,
    getApplicantById,
    applicant,
  } = useRecommenderWorkspaceStore((store) => store);

  const isCounselorRole = userRoles?.includes(Roles.COUNSELOR) && applicant?.counselorRecommenderId;
  const isTeacherRole = userRoles?.includes(Roles.TEACHER) && applicant?.teacherRecommenderId;

  useEffect(() => {
    if (!params?.id) return;

    const applicantId = +params.id;
    if (applicant?.applicantId !== applicantId) {
      const role: RoleEnum = hash.slice(1) as RoleEnum;
      void getApplicantById(applicantId);
      void getApplicantFormsAvailable(role);
    }
  }, [params?.id, hash]);

  useEffect(() => {
    const getData = async () => {
      if (!userRoles) {
        await getRoles();
      }

      if (!userProfile) {
        await getUserProfile();
      }

      await getRecommenderProfile();
    };

    void getData();

    return () => {
      setActiveTabs({ activePageTab: 0, activeApplicationForm: 0 });
    };
  }, []);

  if (!params?.id) {
    window.location.href = ROUTES.RECOMMENDER_SPACE;
    return null;
  }

  const applicantId = +params.id;
  const isFromEcp = new URLSearchParams(location.search).get('isFromEcp') === 'true';

  const studentInfo = applicantFormsAvailable?.applicantFormRequests?.find(
    (student) => student.applicantId === applicantId,
  );
  const legalName = studentInfo?.legalName;

  const tabs = [
    {
      key: 'colleges',
      label: 'Common App Colleges' as ReactNode,
      component: <CommonAppColleges studentName={legalName ?? ''} />,
    },
  ];

  if (isCounselorRole) {
    tabs.push({
      key: 'forms',
      label: 'Application Forms',
      component: <CommonAppApplicationForms studentName={legalName ?? ''} />,
    });
  } else if (isTeacherRole && !isCounselorRole) {
    tabs.push({
      key: 'evaluation',
      label: 'Teacher Evaluation',
      component: <CommonAppTeacherEvaluation />,
    });
  } else {
    tabs.push({
      key: 'loading',
      label: <Skeleton width="8rem" height="2rem" />,
      component: <Skeleton />,
    });
  }

  const handleTabChange = (event: SyntheticEvent, currentTab: number) => {
    setActiveTabs({ activePageTab: currentTab, activeApplicationForm: 0 });
  };

  const renderTabContent = () => {
    return tabs[activeTabs.activePageTab].component;
  };

  return (
    <ThemeProvider theme={brightpathTheme}>
      <ScopedCssBaseline />

      <StyledHeadSectionInner>
        <Breadcrumbs
          links={[
            {
              to: `${ROUTES.RECOMMENDER_SPACE}${isFromEcp ? ECP_HASH : ''}`,
              text: BREADCRUMBS_PAGE_TEXT.RECOMMENDER_WORKSPACE,
            },
          ]}
          currentPage={legalName}
        />

        <StyledCommonAppId>
          <CoreTypography variant="overline">{`Common App ID: ${params.id}`}</CoreTypography>

          <CoreTooltip title="The unique identifier used to identify applicant accounts within the Common App for both first-year and transfer applicants.">
            <CoreBox>
              <InfoIcon />
            </CoreBox>
          </CoreTooltip>
        </StyledCommonAppId>

        <CoreTypography sx={sxStyles.pageTitle} variant="h3">
          {legalName ?? <Skeleton width={200} height={54} />}
        </CoreTypography>

        <StyledHeadInfo>
          <Chip
            title={getGeneralStudentStatus(formsMetadata)}
            color={STATUS_CHIP_COLORS[getGeneralStudentStatus(formsMetadata)]}
            isLoading={formsMetadataLoading}
          />

          {renderInfoItem('School', organizationName)}

          {studentInfo?.deadline
            ? renderInfoItem(
                'Earliest application deadline',
                formatDateWithFallback(studentInfo?.deadline),
                <FlagIcon />,
              )
            : null}
        </StyledHeadInfo>
      </StyledHeadSectionInner>

      <StyledTabs value={activeTabs.activePageTab} onChange={handleTabChange}>
        {tabs.map(({ key, label }) => (
          <StyledTab key={key} label={renderTabItem(label)} />
        ))}
      </StyledTabs>

      <Suspense fallback={null}>{renderTabContent()}</Suspense>
    </ThemeProvider>
  );
};
